import { useState } from 'react';
import { Creator3D } from './components/3d/Creator3D';
import type { Task, TaskPosition } from './types';
import { RecordingProvider } from './context/RecordingContext';
import { GenerationProvider } from './context/GenerationContext';
// Bring in the studio stylesheet so matilda's tailwind utilities
// (bg-black/40, backdrop-blur-xl, glass-panel, etc.) actually apply.
import '../studio.css';

/**
 * Thin wrapper around matilda's Creator3D that:
 *  - supplies the host-app integration props (tasks/todo/byok/etc.) with
 *    project-local defaults, since this project drives the 3D viewer from
 *    its own session/store rather than matilda's harness;
 *  - wraps the viewer in the Recording + Generation providers it depends on.
 *
 * The Matilda chat agent panel is disabled inside Creator3D itself (gated by
 * `{false && ...}` in components/3d/Creator3D.tsx) because this project ships
 * its own composer/agent surface.
 */
export default function Creator3DStandalone({ onClose, fitContainer }: { onClose?: () => void, fitContainer?: boolean }) {
  const [tasks, setTasks] = useState<Task[]>([]);
  const [todoPosition, setTodoPosition] = useState<TaskPosition>({ x: 16, y: 16 });
  const [isTodoMinimized, setIsTodoMinimized] = useState(true);

  return (
    <GenerationProvider>
      <RecordingProvider>
        <div style={{ position: 'absolute', inset: 0 }}>
          <Creator3D
            tasks={tasks}
            setTasks={setTasks}
            todoPosition={todoPosition}
            setTodoPosition={setTodoPosition}
            isTodoMinimized={isTodoMinimized}
            setIsTodoMinimized={setIsTodoMinimized}
            hue={120}
            fitContainer={fitContainer}
          />
          {onClose && (
            <button
              onClick={onClose}
              aria-label="Close 3D viewer"
              style={{
                position: 'absolute',
                top: 12,
                right: 12,
                zIndex: 1000000,
                padding: '6px',
                borderRadius: '50%',
                border: '1px solid var(--line-strong)',
                background: 'var(--glass-strong)',
                color: 'var(--fg)',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <line x1="18" y1="6" x2="6" y2="18"></line>
                <line x1="6" y1="6" x2="18" y2="18"></line>
              </svg>
            </button>
          )}
        </div>
      </RecordingProvider>
    </GenerationProvider>
  );
}
